// ink-component
import React, { KeyboardEvent, FocusEvent, ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import { bemHelper } from '../../helpers';
import setFocus from '../../helpers/dom/setFocus';
import Icon from '../Icon/Icon';
import Control from '../Control/Control';
import { Filter } from './Filter';

export type FilterType = {
  id: string;
  label?: ReactNode;
  name?: string;
  value?: boolean;
  disabled?: boolean;
};

export type CategoryType = {
  category?: string;
  filters?: FilterType[];
};

export interface SearchProps {
  /** Unique identifier */
  id?: string;
  /** Externally controlled ref */
  inputRef?: any;
  /** Default value */
  value?: string;
  /** Placeholder */
  placeholder?: string;
  /** Size modifier */
  size?: 'full' | 'large' | 'half' | 'quarter';
  /** Flag for search with filters */
  hasFilter?: boolean;
  /** Flag for adding a clearable button in the input */
  hasClear?: boolean;
  /** Defines if there's a dropdown and passes the data down */
  dropdownFilters?: (FilterType | CategoryType)[];
  /** Aligns dropdown box to the right of the trigger */
  alignDropdown?: 'right';
  /** filterCallback callback */
  filterCallback?: (id: string, checked: boolean) => void;
  /** onFocus callback. Passes the Event */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  /** onChange callback. Passes the Event */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** onBlur callback. Passes the Event */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** onKeyDown callback. Passes the Event */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** onFilterClick callback */
  onFilterClick?: (event?: Event) => void;
  /** Defines a delay to call the onChange callback in miliseconds */
  onChangeDelay?: number;
  /** Defines if Filter is active (a filter option is selected) */
  isActive?: boolean;
  /** Disables Search's input and Filter */
  isDisabled?: boolean;
  /** HTML type */
  autoFocus?: boolean;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Used to prevent the Dropdown to close when an item is selected */
  preventClose?: boolean;
  /** @ignore */
  dataPrivate?: boolean;
}

/**
 * @purpose Search and filter lists or ledger items
 * @categories form
 * @aka filter
 */
function Search(props: SearchProps) {
  const [value, setValue] = useState<string | undefined>(props.value);
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const newRef = useRef<HTMLInputElement>(null);
  const ref = props.inputRef || newRef;

  useEffect(() => {
    if (props.autoFocus) setFocus(ref);
  }, [props.autoFocus, ref]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const debounceOnChange = debounce(e => {
    if (props?.onChange) props?.onChange(e);
    ref?.current?.focus();
  }, props.onChangeDelay);

  const handleChange = e => {
    e.persist();
    setValue(e.target.value);
    debounceOnChange(e);
  };

  const clearInput = () => {
    setValue('');
    debounceOnChange({ target: { value: '' } });
  };

  const defineClassName = () => {
    const b = bemHelper();

    return b('ink-control--input', {
      clear: Boolean(props.hasClear && value),
      filter: props.hasFilter,
    });
  };

  return (
    <Control
      size={props.size}
      isDisabled={props.isDisabled}
      inputId={props.id}
      dataPrivate={props.dataPrivate}
      data-testid={props['data-testid']}
    >
      <input
        id={props.id}
        ref={ref}
        className={defineClassName()}
        name="search"
        value={value}
        onFocus={props.onFocus}
        onChange={handleChange}
        onBlur={props.onBlur}
        onKeyDown={props.onKeyDown}
        placeholder={props.placeholder}
        autoComplete="off"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={props.autoFocus}
        disabled={props.isDisabled}
        aria-label={props['aria-label']}
      />
      {Boolean(props.hasClear && value && !props.isDisabled) && (
        <button className="ink-control--action ink-control--action__inner" onClick={clearInput} type="button">
          <Icon type="close" meaning="clear search input" size="lg" />
        </button>
      )}
      {props.hasFilter && (
        <Filter
          onClick={props.onFilterClick}
          isActive={props.isActive}
          dropdownFilters={props.dropdownFilters}
          alignDropdown={props.alignDropdown}
          filterCallback={props.filterCallback}
          preventClose={props.preventClose}
          isDisabled={props.isDisabled}
        />
      )}
    </Control>
  );
}

Search.defaultProps = {
  id: undefined,
  inputRef: undefined,
  value: '',
  placeholder: ' ', // Adds a space character so Safari can math baseline alignment
  size: 'large',
  hasFilter: false,
  hasClear: false,
  dropdownFilters: [],
  filterCallback: undefined,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {},
  onKeyDown: () => {},
  onFilterClick: () => {},
  onChangeDelay: 0,
  isActive: false,
  isDisabled: false,
  autoFocus: false,
  preventClose: false,
  'aria-label': 'Search',
  dataPrivate: false,
};

Search.displayName = 'Search';

export default Search;
